import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Navigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import AdminSubNav from "../components/AdminSubNav";
import { useAdminAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Mail, ShieldAlert, ShieldCheck, RefreshCw, PartyPopper, Eye, Loader2, X, Send } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

const STATUS_STYLE = {
    sent:          { label: "sent",          color: "text-[#9a958a]" },
    delivered:     { label: "delivered",     color: "text-emerald-300" },
    opened:        { label: "opened",        color: "text-sky-300" },
    clicked:       { label: "clicked",       color: "text-violet-300" },
    delayed:       { label: "delayed",       color: "text-amber-300" },
    bounced:       { label: "bounced",       color: "text-rose-300" },
    complained:    { label: "complained",    color: "text-rose-300" },
    failed:        { label: "failed",        color: "text-rose-300" },
    "send-failed": { label: "send failed",   color: "text-rose-300" },
    skipped:       { label: "skipped",       color: "text-[#6e6a62]" },
};

const KIND_STYLE = {
    welcome:                   { label: "welcome",           color: "text-[#cfa052]" },
    "share-reminder":          { label: "share reminder",    color: "text-sky-300"   },
    "red-alert":               { label: "red alert",         color: "text-rose-300"  },
    "squad-milestone":         { label: "squad milestone",   color: "text-emerald-300" },
    "squad-milestone-curator": { label: "milestone curator", color: "text-emerald-300" },
    "squad-milestone-test":    { label: "milestone test",    color: "text-emerald-300" },
};

function MilestonePanel() {
    const [status, setStatus] = useState(null);
    const [value, setValue] = useState("");
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [testSending, setTestSending] = useState(false);

    useEffect(() => {
        api.get("/admin/milestone").then(({ data }) => {
            setStatus(data);
            setValue(String(data.milestoneCount));
        }).catch(() => {});
    }, []);

    const save = async () => {
        const n = parseInt(value, 10);
        if (!n || n < 1) { toast.error("Milestone must be a positive number."); return; }
        await saveMilestone(n);
    };

    const saveMilestone = async (n) => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/milestone", { milestoneCount: n });
            setStatus(data);
            setValue(String(data.milestoneCount));
            toast.success(`Milestone set to ${data.milestoneCount} members.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async () => {
        setTestSending(true);
        try {
            const { data } = await api.post("/admin/milestone/test-send");
            toast.success(`Test celebration email sent to ${data.to} — check your inbox.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setTestSending(false);
        }
    };

    const openPreview = async () => {
        setPreviewing(true);
        try {
            const { data } = await api.get("/admin/milestone/preview");
            setPreview(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setPreviewing(false);
        }
    };

    if (!status) return null;
    return (
        <div className="rounded border border-white/10 bg-white/[0.02] p-5 mb-8" data-testid="milestone-panel">
            <div className="flex items-center gap-2 mb-3">
                <PartyPopper className="w-4 h-4 text-[#cfa052]" aria-hidden="true" />
                <h2 className="font-serif-display text-lg text-[#e8e4db] tracking-tight">Squad-complete celebration</h2>
                {status.celebrated ? (
                    <span className="ml-2 font-label text-[9px] tracking-[0.2em] uppercase text-emerald-300 border border-emerald-400/30 bg-emerald-500/10 rounded-full px-2.5 py-0.5" data-testid="milestone-celebrated-badge">
                        Celebrated {status.reachedAt ? new Date(status.reachedAt).toLocaleDateString() : ""}
                    </span>
                ) : (
                    <span className="ml-2 font-label text-[9px] tracking-[0.2em] uppercase text-[#9a958a] border border-white/10 rounded-full px-2.5 py-0.5" data-testid="milestone-pending-badge">
                        {status.memberCount} / {status.milestoneCount} members
                    </span>
                )}
            </div>
            <p className="font-poetic text-sm text-[#9a958a] mb-4 max-w-2xl">
                The moment membership reaches the number below, every member gets a one-time celebration email (and you get a curator note). Changing the number opens a fresh milestone.
            </p>
            <div className="flex flex-wrap items-center gap-3">
                <input
                    type="number"
                    min="1"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="w-28 bg-[#0c0b0a] border border-white/10 rounded px-3 py-2 font-numerals text-lg text-[#cfa052] focus:border-[#cfa052]/60 focus:outline-none"
                    data-testid="milestone-input"
                />
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || String(status.milestoneCount) === value.trim()}
                    className="inline-flex items-center gap-1.5 bg-[#cfa052] text-[#0c0b0a] font-label text-[10px] tracking-[0.25em] uppercase font-semibold px-4 py-2.5 rounded hover:bg-[#e0b569] transition-colors disabled:opacity-40"
                    data-testid="milestone-save-btn"
                >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Save milestone
                </button>
                <button
                    type="button"
                    onClick={openPreview}
                    disabled={previewing}
                    className="inline-flex items-center gap-1.5 border border-white/10 text-[#cfa052] hover:text-[#e0b569] font-label text-[10px] tracking-[0.25em] uppercase px-4 py-2.5 rounded transition-colors disabled:opacity-40"
                    data-testid="milestone-preview-btn"
                >
                    {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} Preview email
                </button>
                <button
                    type="button"
                    onClick={sendTest}
                    disabled={testSending}
                    className="inline-flex items-center gap-1.5 border border-white/10 text-[#9a958a] hover:text-[#cfa052] font-label text-[10px] tracking-[0.25em] uppercase px-4 py-2.5 rounded transition-colors disabled:opacity-40"
                    data-testid="milestone-test-send-btn"
                >
                    {testSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send test to me
                </button>
                {status.celebrated && (() => {
                    const suggested = Math.floor(status.milestoneCount / 25) * 25 + 25;
                    return (
                        <button
                            type="button"
                            onClick={() => saveMilestone(suggested)}
                            disabled={saving}
                            className="inline-flex items-center gap-1.5 border border-emerald-400/30 bg-emerald-500/[0.08] text-emerald-300 hover:bg-emerald-500/[0.15] font-label text-[10px] tracking-[0.25em] uppercase px-4 py-2.5 rounded transition-colors disabled:opacity-40"
                            data-testid="milestone-next-suggest-btn"
                        >
                            <PartyPopper className="w-3 h-3" /> Set next milestone to {suggested}
                        </button>
                    );
                })()}
            </div>

            {status.history?.length > 0 && (
                <div className="mt-5 pt-4 border-t border-white/5" data-testid="milestone-history">
                    <p className="font-label text-[9px] tracking-[0.25em] uppercase text-[#6e6a62] mb-2">Past celebrations</p>
                    <div className="flex flex-wrap gap-2">
                        {status.history.map((h) => (
                            <span
                                key={h.name}
                                className="inline-flex items-center gap-1.5 border border-emerald-400/20 bg-emerald-500/[0.06] text-emerald-300/90 font-label text-[9px] tracking-[0.15em] uppercase rounded-full px-3 py-1"
                                data-testid={`milestone-history-${h.name}`}
                            >
                                <PartyPopper className="w-3 h-3" aria-hidden="true" />
                                {h.name.replace("squad-complete-", "")} members · {h.reachedAt ? new Date(h.reachedAt).toLocaleDateString() : "—"}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {preview && createPortal(
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" data-testid="milestone-preview-modal" onClick={() => setPreview(null)}>
                    <div className="bg-[#151311] border border-white/10 rounded-sm max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
                            <div>
                                <p className="font-label text-[9px] tracking-[0.2em] uppercase text-[#6e6a62]">Subject</p>
                                <p className="font-poetic text-sm text-[#e8e4db]" data-testid="milestone-preview-subject">{preview.subject}</p>
                            </div>
                            <button type="button" onClick={() => setPreview(null)} aria-label="Close preview" data-testid="milestone-preview-close" className="p-1.5 text-[#6e6a62] hover:text-[#e8e4db] transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <iframe
                            title="Milestone email preview"
                            srcDoc={preview.html}
                            sandbox=""
                            className="w-full flex-1 min-h-[480px] bg-[#0a0908]"
                            data-testid="milestone-preview-frame"
                        />
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default function AdminEmails() {
    const { admin } = useAdminAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/admin/email-events");
            setData(data);
            setError("");
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (admin === null) return null;
    if (!admin) return <Navigate to="/login" replace />;

    return (
        <PortalLayout>
            <section
                className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-32"
                data-testid="admin-emails-page"
            >
                <div className="mb-10">
                    <span className="font-label text-[11px] text-[#cfa052]">Admin · emails</span>
                    <h1
                        className="mt-4 font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]"
                        data-testid="admin-emails-title"
                    >
                        Email <span className="italic">deliverability</span>
                    </h1>
                    <p className="mt-4 font-poetic text-[#9a958a] max-w-2xl">
                        Every email Angel Select has sent (welcome, share reminder, curator alert) plus
                        the live status fed back by Resend's webhook. Use this to spot bounces, spam complaints,
                        or quiet failures before they become member-support tickets.
                    </p>
                </div>

                <AdminSubNav />

                <MilestonePanel />

                {loading ? (
                    <div className="text-center py-32 font-poetic text-[#9a958a]" data-testid="admin-emails-loading">
                        opening the mailbox…
                    </div>
                ) : error ? (
                    <div
                        className="rounded border border-rose-500/30 bg-rose-500/5 p-6 font-poetic text-rose-300"
                        data-testid="admin-emails-error"
                    >
                        {error}
                    </div>
                ) : (
                    <>
                        <div
                            className={`rounded border ${data.webhookSecretConfigured ? "border-emerald-400/30 bg-emerald-500/5" : "border-amber-400/30 bg-amber-500/5"} p-5 mb-8 flex items-start gap-3`}
                            data-testid="admin-emails-webhook-status"
                        >
                            {data.webhookSecretConfigured ? (
                                <ShieldCheck className="w-5 h-5 text-emerald-300 mt-0.5 flex-none" />
                            ) : (
                                <ShieldAlert className="w-5 h-5 text-amber-300 mt-0.5 flex-none" />
                            )}
                            <div className="font-poetic text-sm">
                                {data.webhookSecretConfigured ? (
                                    <span className="text-emerald-200">
                                        Webhook signing is verified — only Resend can update statuses below.
                                    </span>
                                ) : (
                                    <span className="text-amber-200">
                                        <code className="bg-black/40 px-1.5 py-0.5 rounded text-[12px] text-amber-100">RESEND_WEBHOOK_SECRET</code> is not configured. Statuses will still update, but the endpoint accepts unsigned events. Add the secret in the Resend dashboard → Webhooks for production.
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="admin-emails-summary">
                            {Object.entries(data.summary || {}).map(([k, v]) => {
                                const style = STATUS_STYLE[k] || { label: k, color: "text-[#9a958a]" };
                                return (
                                    <span
                                        key={k}
                                        className="font-label text-[10px] tracking-[0.25em] uppercase border border-white/10 rounded-full px-3 py-1"
                                        data-testid={`admin-emails-summary-${k}`}
                                    >
                                        <span className={style.color}>{style.label}</span>
                                        <span className="text-[#9a958a]"> · {v}</span>
                                    </span>
                                );
                            })}
                            <button
                                type="button"
                                onClick={() => { setLoading(true); load(); }}
                                className="ml-auto inline-flex items-center gap-1.5 font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052] hover:text-[#e8e4db] transition-colors"
                                data-testid="admin-emails-refresh"
                            >
                                <RefreshCw className="w-3 h-3" /> refresh
                            </button>
                        </div>

                        {data.items.length === 0 ? (
                            <div className="text-center py-32" data-testid="admin-emails-empty">
                                <Mail className="w-8 h-8 mx-auto text-[#6e6a62] mb-3" />
                                <p className="font-poetic text-[#9a958a]">No emails sent yet.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto" data-testid="admin-emails-table-wrap">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="border-b border-white/10">
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Sent</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Kind</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">To</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Subject</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Status</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Angel</th>
                                            <th className="font-label text-[10px] text-[#cfa052] py-3">Resend id</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5 font-poetic" data-testid="admin-emails-list">
                                        {data.items.map((row) => {
                                            const status = STATUS_STYLE[row.status] || { label: row.status, color: "text-[#9a958a]" };
                                            const kind = KIND_STYLE[row.kind] || { label: row.kind, color: "text-[#9a958a]" };
                                            return (
                                                <tr key={row.id} className="text-[#e8e4db]" data-testid={`admin-emails-row-${row.id}`}>
                                                    <td className="py-3 pr-6 text-[#9a958a] whitespace-nowrap">{formatDate(row.sent_at)}</td>
                                                    <td className={`py-3 pr-6 font-label text-[10px] tracking-[0.25em] uppercase ${kind.color}`}>
                                                        {kind.label}
                                                    </td>
                                                    <td className="py-3 pr-6 text-[#9a958a] break-all max-w-[180px]">{row.to}</td>
                                                    <td className="py-3 pr-6 text-[#e8e4db] break-all max-w-[260px]">{row.subject}</td>
                                                    <td className={`py-3 pr-6 font-label text-[10px] tracking-[0.25em] uppercase ${status.color}`}>
                                                        {status.label}
                                                    </td>
                                                    <td className="py-3 pr-6">
                                                        {row.angelNumber ? (
                                                            <span className="font-numerals text-lg text-[#cfa052]">{row.angelNumber}</span>
                                                        ) : (
                                                            <span className="text-[#6e6a62]">—</span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 text-[#6e6a62] font-label text-[10px] break-all">
                                                        {row.resend_id || (row.error ? <span className="text-rose-400/80">{row.error}</span> : "—")}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </section>
        </PortalLayout>
    );
}
